import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CustomersAdd from "./CustomersAdd";
import CustomersEdit from "./CustomersEdit";
import ApiServices from "../../../Services/ApiServices/ApiServices";

const CustomersList = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const debounceRef = useRef(null);

  const fetchCustomers = async (q = "") => {
    setLoading(true);
    try {
      const url = q ? `customers/?search=${encodeURIComponent(q)}` : "customers/";
      const res = await ApiServices.get(url);
      const data = res?.data ?? res;
      setCustomers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      let msg = "Failed to fetch customers.";
      if (err?.response?.data?.detail) msg = err.response.data.detail;
      else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchCustomers(search);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (c) => {
    setSelectedCustomer(c);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setSelectedCustomer(null);
    setOpenEdit(false);
  };

  const handleDeleteClick = (c) => {
    setDeleteTarget(c);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setConfirmOpen(false);
    setLoading(true);
    try {
      await ApiServices.delete(`customers/${deleteTarget.id}/`);
      setSnack({ open: true, message: "Customer deleted", severity: "success" });
      await fetchCustomers(search);
    } catch (err) {
      console.error(err);
      let msg = "Delete failed.";
      if (err?.response?.data?.detail) msg = err.response.data.detail;
      else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
      setDeleteTarget(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setDeleteTarget(null);
  };

  const onAddSuccess = async (msg = "Customer added") => {
    setSnack({ open: true, message: msg, severity: "success" });
    setOpenAdd(false);
    await fetchCustomers(search);
  };

  const onEditSuccess = async (msg = "Customer updated") => {
    setSnack({ open: true, message: msg, severity: "success" });
    handleCloseEdit();
    await fetchCustomers(search);
  };

  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  return (
    <Box sx={containerStyles}>
      <style>{`
        .customers-header {
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:12px;
          margin-bottom:16px;
          flex-wrap:wrap;
        }
        .search-field { max-width:420px; width:100%; }
        .add-btn { min-width:160px; }
      `}</style>

      <Box className="customers-header">
        <Typography variant="h5" sx={{ color: "#094c9f", fontWeight: 700 }}>
          Customers
        </Typography>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            className="search-field"
            placeholder="Search customers..."
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            className="add-btn"
            sx={{ backgroundColor: "#094c9f", "&:hover": { backgroundColor: "#083b82" } }}
          >
            Add New Customer
          </Button>
        </Box>
      </Box>

      <Paper sx={{ width: "100%", overflow: "hidden" }} elevation={2}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: "64vh", overflowX: "auto" }}>
            <Table stickyHeader sx={{ minWidth: 800, tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: "60px" }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "160px" }}>First Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "160px" }}>Last Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "220px" }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "140px" }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "28%" }}>Address</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, width: "120px" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {customers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No customers found.
                    </TableCell>
                  </TableRow>
                ) : (
                  customers.map((c, idx) => (
                    <TableRow key={c.id} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {c.first_name}
                      </TableCell>
                      <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {c.last_name}
                      </TableCell>
                      <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {c.email}
                      </TableCell>
                      <TableCell>{c.phone}</TableCell>
                      <TableCell
                        sx={{
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          maxWidth: { xs: "140px", sm: "260px", md: "420px" },
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {c.address} {c.zip_code ? `• ${c.zip_code}` : ""}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => handleOpenEdit(c)} aria-label="edit" size="small">
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteClick(c)} aria-label="delete" size="small">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add dialog */}
      <Dialog open={openAdd} onClose={handleCloseAdd} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Customer</DialogTitle>
        <DialogContent>
          <CustomersAdd onSuccess={onAddSuccess} onCancel={handleCloseAdd} />
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={openEdit} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Customer</DialogTitle>
        <DialogContent>
          {selectedCustomer && (
            <CustomersEdit customer={selectedCustomer} onSuccess={onEditSuccess} onCancel={handleCloseEdit} />
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={confirmOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirm delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteTarget?.first_name} {deleteTarget?.last_name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4500} onClose={closeSnack}>
        <Alert onClose={closeSnack} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomersList;

const containerStyles = {
  p: 3,
  width: "100%",
  minHeight: "70vh",
  background: "#fff",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: 2,
};
